import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

import styles from './QuizPdfViewer.module.scss';
import { buildGoogleGviewEmbedUrl, buildMobileNativePdfSrc, buildPdfStreamUrl, buildSameOriginPdfStreamUrl, normalizePdfUrlForViewer, resolvePublicPdfUrlForGview } from '../../assets/utils/pdfViewerUrl';
import { getPdfViewerMode } from '../../assets/utils/pdfViewerEnv';
import { isAndroidDevice, isIosDevice, shouldPreferMobilePdfRenderer } from '../../assets/utils/viewport';
import PdfToolbar from './PdfToolbar';

const WORKER_SRC = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;
if (typeof window !== 'undefined' && pdfjs?.GlobalWorkerOptions) {
    pdfjs.GlobalWorkerOptions.workerSrc = WORKER_SRC;
}

const ZOOM_STEP = 0.15;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2.5;
const PDF_GET_DOCUMENT_OPTIONS = { disableRange: true, disableStream: true };
const WORKER_DESTROYED_HINT = /worker was destroyed|destroyed|cancelled/i;

const PDF_VIEWER_APP_MODE = getPdfViewerMode();

function computeMostVisiblePage(viewportEl, numPages) {
    if (!viewportEl || !numPages) return 1;
    const vpRect = viewportEl.getBoundingClientRect();
    let best = 1;
    let bestVisible = -1;
    for (let i = 1; i <= numPages; i += 1) {
        const el = document.getElementById(`quiz-pdf-page-${i}`);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        const visible = Math.max(0, Math.min(r.bottom, vpRect.bottom) - Math.max(r.top, vpRect.top));
        if (visible > bestVisible) {
            bestVisible = visible;
            best = i;
        }
    }
    return best;
}

function scrollElementIntoViewport(viewportEl, targetEl, padding = 8) {
    if (!viewportEl || !targetEl) return;
    const vpRect = viewportEl.getBoundingClientRect();
    const elRect = targetEl.getBoundingClientRect();
    const nextTop = viewportEl.scrollTop + (elRect.top - vpRect.top) - padding;
    viewportEl.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' });
}

function QuizPdfReactPdfMode({ fileUrl, downloadUrl }) {
    const [numPages, setNumPages] = useState(null);
    const [scale, setScale] = useState(1);
    const [fitScale, setFitScale] = useState(1);
    const [pageInput, setPageInput] = useState('1');
    const [loadError, setLoadError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [documentMountReady, setDocumentMountReady] = useState(false);
    const [remountToken, setRemountToken] = useState(0);
    const workerRetryRef = useRef(0);

    const viewportRef = useRef(null);
    const firstPageWidthRef = useRef(null);
    const didAutoFitRef = useRef(false);
    const skipScrollPageSyncRef = useRef(false);
    const dpr = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    const resolvedFileUrl = useMemo(() => normalizePdfUrlForViewer(fileUrl), [fileUrl]);
    const file = useMemo(() => (resolvedFileUrl ? resolvedFileUrl : null), [resolvedFileUrl]);

    useEffect(() => {
        setIsLoading(true);
        setLoadError(null);
        setNumPages(null);
        firstPageWidthRef.current = null;
        didAutoFitRef.current = false;
        workerRetryRef.current = 0;
        setPageInput('1');
        setScale(1);
        setDocumentMountReady(false);

        if (!fileUrl) return undefined;

        const delayMs = 120;
        const t = window.setTimeout(() => {
            setDocumentMountReady(true);
        }, delayMs);

        return () => {
            window.clearTimeout(t);
            setDocumentMountReady(false);
        };
    }, [fileUrl]);

    const recomputeFitScale = useCallback(() => {
        const vp = viewportRef.current;
        const w = firstPageWidthRef.current;
        if (!vp || !w || w <= 0) return;
        const padding = 32;
        const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, (vp.clientWidth - padding) / w));
        setFitScale(next);
    }, []);

    useLayoutEffect(() => {
        recomputeFitScale();
    }, [recomputeFitScale, numPages]);

    useEffect(() => {
        const vp = viewportRef.current;
        if (!vp || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => recomputeFitScale());
        ro.observe(vp);
        return () => ro.disconnect();
    }, [recomputeFitScale]);

    const onDocumentLoadSuccess = useCallback(({ numPages: n }) => {
        workerRetryRef.current = 0;
        setNumPages(n);
        setLoadError(null);
        setIsLoading(false);
        setPageInput('1');
    }, []);

    const onDocumentLoadError = useCallback((err) => {
        const msg = err?.message || String(err || '');
        if (WORKER_DESTROYED_HINT.test(msg) && workerRetryRef.current < 2) {
            workerRetryRef.current += 1;
            setNumPages(null);
            setDocumentMountReady(false);
            setIsLoading(true);
            setLoadError(null);
            window.setTimeout(() => {
                setRemountToken((k) => k + 1);
                setDocumentMountReady(true);
            }, 200);
            return;
        }
        setLoadError(msg || 'بارگذاری فایل PDF ممکن نیست.');
        setIsLoading(false);
    }, []);

    const onFirstPageLoadSuccess = useCallback(
        (page) => {
            try {
                const viewport = page.getViewport({ scale: 1 });
                firstPageWidthRef.current = viewport.width;
                const vp = viewportRef.current;
                if (vp && viewport.width > 0) {
                    const padding = 32;
                    const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, (vp.clientWidth - padding) / viewport.width));
                    const run = () => {
                        setFitScale(next);
                        if (!didAutoFitRef.current) {
                            didAutoFitRef.current = true;
                            setScale(next);
                        }
                    };
                    window.requestAnimationFrame(run);
                } else {
                    window.requestAnimationFrame(() => recomputeFitScale());
                }
            } catch {
                firstPageWidthRef.current = null;
            }
        },
        [recomputeFitScale],
    );

    const scrollToPage = useCallback(
        (p) => {
            const safe = Math.min(Math.max(1, p), numPages || 1);
            const vp = viewportRef.current;
            const el = document.getElementById(`quiz-pdf-page-${safe}`);
            setPageInput(String(safe));
            if (vp && el) {
                scrollElementIntoViewport(vp, el, 8);
            }
        },
        [numPages],
    );

    useEffect(() => {
        const vp = viewportRef.current;
        if (!vp || !numPages) return undefined;

        let raf = 0;
        const syncFromScroll = () => {
            if (skipScrollPageSyncRef.current) return;
            const p = computeMostVisiblePage(vp, numPages);
            setPageInput((prev) => (String(p) === prev ? prev : String(p)));
        };

        const onScroll = () => {
            if (raf) cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                raf = 0;
                syncFromScroll();
            });
        };

        vp.addEventListener('scroll', onScroll, { passive: true });
        syncFromScroll();

        return () => {
            vp.removeEventListener('scroll', onScroll);
            if (raf) cancelAnimationFrame(raf);
        };
    }, [numPages, scale, fileUrl, remountToken]);

    const zoomIn = () => setScale((s) => Math.min(ZOOM_MAX, Math.round((s + ZOOM_STEP) * 100) / 100));
    const zoomOut = () => setScale((s) => Math.max(ZOOM_MIN, Math.round((s - ZOOM_STEP) * 100) / 100));
    const fitWidth = () => setScale(fitScale > 0 ? fitScale : 1);

    const goPrev = () => {
        const cur = Math.max(1, parseInt(pageInput, 10) || 1);
        scrollToPage(cur - 1);
    };

    const goNext = () => {
        const cur = Math.min(numPages || 1, parseInt(pageInput, 10) || 1);
        scrollToPage(cur + 1);
    };

    const onPageInputChange = (e) => setPageInput(e.target.value.replace(/\D/g, ''));

    const onPageInputFocus = useCallback(() => {
        skipScrollPageSyncRef.current = true;
    }, []);

    const onPageInputBlur = () => {
        skipScrollPageSyncRef.current = false;
        if (!numPages) return;
        let p = parseInt(pageInput, 10);
        if (Number.isNaN(p) || p < 1) p = 1;
        if (p > numPages) p = numPages;
        setPageInput(String(p));
        scrollToPage(p);
    };

    const onPageInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            onPageInputBlur();
        }
    };

    const pct = Math.round(scale * 100);

    if (!fileUrl) {
        return <p className={styles.hint}>آدرس فایل سوال در دسترس نیست.</p>;
    }

    return (
        <div className={styles.wrap}>
            <PdfToolbar
                hidePagination={false}
                pct={pct}
                onZoomIn={zoomIn}
                onZoomOut={zoomOut}
                onFitWidth={fitWidth}
                disableZoom={!numPages}
                pageInput={pageInput}
                pageCountLabel={numPages ?? '…'}
                onPageInputChange={onPageInputChange}
                onPageInputFocus={onPageInputFocus}
                onPageInputBlur={onPageInputBlur}
                onPageInputKeyDown={onPageInputKeyDown}
                onGoPrev={goPrev}
                onGoNext={goNext}
                disablePrev={!numPages || (parseInt(pageInput, 10) || 1) <= 1}
                disableNext={!numPages || (parseInt(pageInput, 10) || 1) >= (numPages || 1)}
                disablePageInput={!numPages}
                onReload={undefined}
                openInNewTabUrl={downloadUrl || fileUrl}
            />

            <div className={styles.viewport} ref={viewportRef}>
                {(isLoading || !documentMountReady) && !loadError && (
                    <div className={styles.loading}>در حال بارگذاری PDF…</div>
                )}
                {loadError ? (
                    <div className={styles.error}>
                        {loadError}
                        {downloadUrl ? (
                            <>
                                <br />
                                <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
                                    باز کردن یا دانلود فایل
                                </a>
                            </>
                        ) : null}
                    </div>
                ) : documentMountReady && file ? (
                    <Document
                        key={`${fileUrl}__${remountToken}`}
                        file={file}
                        options={PDF_GET_DOCUMENT_OPTIONS}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={onDocumentLoadError}
                        loading={null}
                    >
                        {numPages
                            ? Array.from({ length: numPages }, (_, i) => {
                                  const pageNumber = i + 1;
                                  return (
                                      <div key={pageNumber} id={`quiz-pdf-page-${pageNumber}`} className={styles.pageAnchor}>
                                          <div className={styles.pageSheet}>
                                              <Page
                                                  pageNumber={pageNumber}
                                                  scale={scale}
                                                  devicePixelRatio={dpr}
                                                  renderTextLayer={false}
                                                  renderAnnotationLayer
                                                  onLoadSuccess={pageNumber === 1 ? onFirstPageLoadSuccess : undefined}
                                              />
                                          </div>
                                      </div>
                                  );
                              })
                            : null}
                    </Document>
                ) : null}
            </div>
        </div>
    );
}

function buildNativePdfIframeSrc(baseUrl, page = 1, { mobile = false } = {}) {
    if (!baseUrl) return '';
    if (mobile) {
        // روی موبایل URL ساده — نمایشگر native مرورگر خودش fit/zoom را انجام می‌دهد.
        return baseUrl;
    }
    const p = Math.max(1, parseInt(String(page), 10) || 1);
    return `${baseUrl}#page=${p}&navpanes=0`;
}

function QuizPdfIframeMode({ fileUrl, downloadUrl, mobileLayout = false }) {
    const [loadError, setLoadError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [documentMountReady, setDocumentMountReady] = useState(false);
    const [iframeSrc, setIframeSrc] = useState('');

    /** در iframe مرورگر خودش PDF را لود می‌کند؛ پروکسی لازم نیست. */
    const basePdfUrl = useMemo(() => {
        if (!fileUrl) return '';
        const s = String(fileUrl).trim();
        const i = s.indexOf('#');
        return i >= 0 ? s.slice(0, i) : s;
    }, [fileUrl]);

    const buildSrc = useCallback(
        (page) => buildNativePdfIframeSrc(basePdfUrl, page, { mobile: mobileLayout }),
        [basePdfUrl, mobileLayout],
    );

    useEffect(() => {
        setIsLoading(true);
        setLoadError(null);
        setIframeSrc('');
        setDocumentMountReady(false);

        if (!fileUrl) return undefined;

        const delayMs = 120;
        const t = window.setTimeout(() => {
            setDocumentMountReady(true);
            if (basePdfUrl) {
                setIframeSrc(buildSrc(1));
            }
        }, delayMs);

        return () => {
            window.clearTimeout(t);
            setDocumentMountReady(false);
        };
    }, [fileUrl, basePdfUrl, buildSrc]);

    if (!fileUrl) {
        return <p className={styles.hint}>آدرس فایل سوال در دسترس نیست.</p>;
    }

    const openUrl = downloadUrl || basePdfUrl || fileUrl;

    return (
        <div className={`${styles.wrap} ${styles.wrapIframePlain} ${mobileLayout ? styles.wrapMobileNative : ''}`}>
            {mobileLayout ? (
                <div className={styles.mobileNativeBar}>
                    <a
                        className={styles.mobileNativeOpenBtn}
                        href={openUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        مشاهده PDF با کیفیت کامل
                    </a>
                    <p className={styles.mobileNativeHint}>
                        برای خوانایی بهتر متن فارسی، در صورت نیاز PDF را در نمایشگر مرورگر باز کنید.
                    </p>
                </div>
            ) : null}
            <div className={`${styles.viewport} ${styles.viewportIframePlain} ${mobileLayout ? styles.viewportMobileNative : ''}`}>
                {isLoading && !loadError && documentMountReady && (
                    <div className={styles.loading}>در حال بارگذاری PDF…</div>
                )}
                {loadError ? (
                    <div className={styles.error}>
                        {loadError}
                        {downloadUrl ? (
                            <>
                                <br />
                                <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
                                    باز کردن یا دانلود فایل
                                </a>
                            </>
                        ) : null}
                    </div>
                ) : null}
                {documentMountReady && iframeSrc ? (
                    <>
                        <iframe
                            key={iframeSrc}
                            title="سوال آزمون PDF"
                            src={iframeSrc}
                            className={mobileLayout ? styles.pdfIframeMobile : styles.pdfIframeTall}
                            onLoad={() => setIsLoading(false)}
                            onError={() => {
                                setLoadError('نمایش PDF در iframe ممکن نیست.');
                                setIsLoading(false);
                            }}
                        />
                        {mobileLayout && !loadError && !isLoading ? (
                            <p className={styles.mobileNativeFallback}>
                                PDF نمایش داده نمی‌شود؟{' '}
                                <a href={openUrl} target="_blank" rel="noopener noreferrer">
                                    باز کردن فایل
                                </a>
                            </p>
                        ) : null}
                    </>
                ) : null}
            </div>
        </div>
    );
}

function QuizPdfNativeMobile({ fileUrl }) {
    const isIos = useMemo(() => isIosDevice(), []);
    const isAndroid = useMemo(() => isAndroidDevice(), []);
    const streamUrl = useMemo(() => buildPdfStreamUrl(fileUrl), [fileUrl]);
    const iosSrc = useMemo(() => buildMobileNativePdfSrc(fileUrl), [fileUrl]);
    const androidSrc = useMemo(() => buildSameOriginPdfStreamUrl(fileUrl), [fileUrl]);

    if (!fileUrl) {
        return <p className={styles.hint}>آدرس فایل سوال در دسترس نیست.</p>;
    }

    if (isIos) {
        if (!iosSrc) {
            return <p className={styles.hint}>آدرس فایل سوال در دسترس نیست.</p>;
        }

        return (
            <div className={styles.wrapGviewMobile}>
                <embed src={iosSrc} type="application/pdf" className={styles.nativePdfEmbed} title="سوال آزمون PDF" />
            </div>
        );
    }

    if (isAndroid) {
        if (!androidSrc) {
            return <p className={styles.hint}>آدرس فایل سوال در دسترس نیست.</p>;
        }

        return (
            <div className={styles.wrapGviewMobile}>
                <embed src={androidSrc} type="application/pdf" className={styles.nativePdfEmbed} title="سوال آزمون PDF" />
                <a
                    className={styles.androidPdfFallback}
                    href={streamUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    PDF نمایش داده نمی‌شود؟ باز کردن در مرورگر
                </a>
            </div>
        );
    }

    if (!iosSrc) {
        return <p className={styles.hint}>آدرس فایل سوال در دسترس نیست.</p>;
    }

    return (
        <div className={styles.wrapGviewMobile}>
            <embed src={iosSrc} type="application/pdf" className={styles.nativePdfEmbed} title="سوال آزمون PDF" />
        </div>
    );
}

function QuizPdfGviewMode({ fileUrl, downloadUrl }) {
    const [visualScale, setVisualScale] = useState(1);
    const [loadError, setLoadError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [documentMountReady, setDocumentMountReady] = useState(false);
    const [iframeKey, setIframeKey] = useState(0);

    const gviewSrc = useMemo(() => buildGoogleGviewEmbedUrl(fileUrl), [fileUrl]);
    const openUrl = useMemo(
        () => downloadUrl || resolvePublicPdfUrlForGview(fileUrl) || fileUrl,
        [downloadUrl, fileUrl],
    );

    useEffect(() => {
        setIsLoading(true);
        setLoadError(null);
        setVisualScale(1);
        setDocumentMountReady(false);

        if (!fileUrl) return undefined;

        const delayMs = 120;
        const t = window.setTimeout(() => {
            setDocumentMountReady(true);
        }, delayMs);

        return () => {
            window.clearTimeout(t);
            setDocumentMountReady(false);
        };
    }, [fileUrl]);

    const zoomIn = () => setVisualScale((s) => Math.min(ZOOM_MAX, Math.round((s + ZOOM_STEP) * 100) / 100));
    const zoomOut = () => setVisualScale((s) => Math.max(ZOOM_MIN, Math.round((s - ZOOM_STEP) * 100) / 100));
    const fitWidth = () => setVisualScale(1);

    const onReload = useCallback(() => {
        setIsLoading(true);
        setIframeKey((k) => k + 1);
    }, []);

    const pct = Math.round(visualScale * 100);

    if (!fileUrl) {
        return <p className={styles.hint}>آدرس فایل سوال در دسترس نیست.</p>;
    }

    return (
        <div className={styles.wrap}>
            <PdfToolbar
                hidePagination
                pct={pct}
                onZoomIn={zoomIn}
                onZoomOut={zoomOut}
                onFitWidth={fitWidth}
                disableZoom={!documentMountReady || !gviewSrc}
                onReload={onReload}
                openInNewTabUrl={openUrl}
            />

            <div className={styles.viewport}>
                {isLoading && !loadError && documentMountReady && (
                    <div className={styles.loading}>در حال بارگذاری نمایشگر…</div>
                )}
                {loadError ? (
                    <div className={styles.error}>
                        {loadError}
                        {openUrl ? (
                            <>
                                <br />
                                <a href={openUrl} target="_blank" rel="noopener noreferrer">
                                    باز کردن یا دانلود فایل
                                </a>
                            </>
                        ) : null}
                    </div>
                ) : null}
                {documentMountReady && gviewSrc ? (
                    <div className={styles.iframeZoomWrap}>
                        <div
                            className={styles.iframeZoomInner}
                            style={{
                                transform: `scale(${visualScale})`,
                                transformOrigin: 'top center',
                            }}
                        >
                            <iframe
                                key={iframeKey}
                                title="سوال آزمون PDF (Google)"
                                src={gviewSrc}
                                className={styles.pdfIframe}
                                onLoad={() => setIsLoading(false)}
                                onError={() => {
                                    setLoadError('نمایش PDF از طریق سرویس گوگل ممکن نیست.');
                                    setIsLoading(false);
                                }}
                            />
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}

export default function QuizPdfViewer({ fileUrl, downloadUrl, ...rest }) {
    const [preferMobileRenderer, setPreferMobileRenderer] = useState(false);
    const streamDownloadUrl = useMemo(
        () => buildPdfStreamUrl(downloadUrl || fileUrl),
        [downloadUrl, fileUrl],
    );

    useEffect(() => {
        const sync = () => setPreferMobileRenderer(shouldPreferMobilePdfRenderer());
        sync();
        window.addEventListener('resize', sync);
        return () => window.removeEventListener('resize', sync);
    }, []);

    const viewerProps = {
        fileUrl,
        downloadUrl: streamDownloadUrl,
        ...rest,
    };

    /** موبایل: نمایشگر native مرورگر — pdf.js برای فارسی مناسب نیست */
    if (preferMobileRenderer) {
        return <QuizPdfNativeMobile fileUrl={fileUrl} />;
    }

    switch (PDF_VIEWER_APP_MODE) {
        case 'react-pdf':
            return <QuizPdfReactPdfMode {...viewerProps} />;
        case 'gview':
            return <QuizPdfGviewMode {...viewerProps} />;
        default:
            return <QuizPdfIframeMode {...viewerProps} />;
    }
}
